import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  listPages, createPage, archivePage, createEventOverride,
  type CmsPage, type PageKind,
} from '../../lib/cmsAdmin';
import { searchEventsForOpportunity, type SelectableEvent } from '../../lib/admin';
import { listOrganizations, type Organization } from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import './AdminPages.css';
import './AdminCms.css';

/**
 * Pages: a layout for exactly one thing.
 *
 * ── AN OVERRIDE IS AN EXCEPTION, NOT THE NORM ────────────────────────────────────────
 *
 * Every event renders through a template. A page override exists for the event that needs
 * something its template cannot give it, and it costs the thing templates are for: a
 * change to the template no longer reaches that event. The list shows which entity each
 * override belongs to so that trade is visible rather than discovered later, when a
 * platform-wide change mysteriously skips one event.
 */
export function AdminCmsPages() {
  const { organizationId } = useAdminScope();
  const [pages, setPages] = useState<CmsPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [pageKind, setPageKind] = useState<PageKind | ''>('');
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  usePageChrome({
    title: 'Pages',
    subtitle: 'Layouts for a single entity or a single URL. Everything else renders '
      + 'through a template.',
  });

  useEffect(() => {
    setPages(null);
    listPages({ organizationId, pageKind: pageKind || null })
      .then(setPages)
      .catch((err: unknown) => setError(err instanceof ApiError
        ? err.message : 'The pages could not be loaded.'));
  }, [organizationId, pageKind, reload]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pages ?? [];
    return (pages ?? []).filter((p) => p.name.toLowerCase().includes(term)
      || (p.slug ?? '').toLowerCase().includes(term));
  }, [pages, search]);

  async function remove(page: CmsPage) {
    if (!window.confirm(
      page.pageKind === 'entity_override'
        ? `Remove the override for "${page.name}"? That event goes back to rendering `
          + 'through its template, and starts receiving template changes again.'
        : `Archive "${page.name}"? The page stops being served.`,
    )) return;
    setBusyId(page.id);
    try {
      await archivePage(page.id);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That page could not be archived.');
    } finally { setBusyId(null); }
  }

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="adm-filters">
        <input className="adm-select" type="search" placeholder="Search pages…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          aria-label="Search pages" />
        <select className="adm-select" value={pageKind} aria-label="Filter by kind"
          onChange={(e) => setPageKind(e.target.value as PageKind | '')}>
          <option value="">All pages</option>
          <option value="entity_override">Entity overrides</option>
          <option value="standalone">Standalone</option>
        </select>
        <Link className="button button--primary" to="/admin/content/pages/new">New page</Link>
      </div>

      {!pages && !error && <p className="adm-lede">Loading pages…</p>}
      {pages && shown.length === 0 && (
        <p className="adm-lede">No pages {search || pageKind ? 'match that filter' : 'yet'}.</p>
      )}

      {shown.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th><th>Kind</th><th>Scope</th><th>Live</th><th>Draft</th>
              <th>Created</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id}>
                <td>
                  <Link to={`/admin/content/pages/${p.id}`}>{p.name}</Link>
                  {p.slug && <div className="adm-muted">/{p.slug}</div>}
                  {p.entityId && (
                    <div className="adm-muted">
                      Overrides <Link to={`/admin/events/${p.entityId}`}>this event</Link>
                    </div>
                  )}
                </td>
                <td>{p.pageKind === 'entity_override' ? 'Override' : 'Standalone'}</td>
                <td>{p.scope === 'platform' ? 'Platform' : 'Organization'}</td>
                <td>{p.publishedVersion ? `v${p.publishedVersion}` : <span className="adm-muted">never</span>}</td>
                <td>{p.draftVersion ? <span className="badge badge--pending">v{p.draftVersion}</span> : '—'}</td>
                <td className="adm-nowrap">{new Date(p.createdAt).toLocaleDateString()}</td>
                <td className="adm-actions">
                  <button type="button" className="button button--ghost adm-btn-sm" disabled={busyId === p.id}
                    onClick={() => { void remove(p); }}>
                    {p.pageKind === 'entity_override' ? 'Remove override' : 'Archive'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Create a page.
 *
 * An event override is created through the event's own endpoint, not by posting a page
 * with an organization id — the event decides which organization owns the result, so
 * naming one here could not make a cross-tenant page even if somebody tried.
 */
export function AdminCmsPageNew() {
  const navigate = useNavigate();
  const { organizationId } = useAdminScope();

  const [kind, setKind] = useState<PageKind>('entity_override');
  const [events, setEvents] = useState<SelectableEvent[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [eventId, setEventId] = useState('');
  const [startFrom, setStartFrom] = useState<'resolved' | 'blank'>('resolved');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [org, setOrg] = useState(organizationId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageChrome({
    title: 'New page',
    breadcrumb: [
      { label: 'Content & design' },
      { label: 'Pages', to: '/admin/content/pages' },
      { label: 'New' },
    ],
  });

  useEffect(() => {
    searchEventsForOpportunity().then(setEvents).catch(() => setEvents([]));
    listOrganizations().then(setOrgs).catch(() => setOrgs([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = kind === 'entity_override'
        ? await createEventOverride(eventId, { name: name.trim() || null, startFrom })
        : await createPage({
          pageKind: 'standalone',
          scope: org ? 'organization' : 'platform',
          organizationId: org || null,
          slug: slug.trim() || null,
          name: name.trim(),
        });
      navigate(`/admin/content/pages/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The page could not be created.');
      setBusy(false);
    }
  }

  return (
    <div className="adm-page">
      <form className="adm-form cms-form" onSubmit={submit}>
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <label className="field">
          <span className="field__label">What kind of page</span>
          <select className="field__input" value={kind}
            onChange={(e) => setKind(e.target.value as PageKind)}>
            <option value="entity_override">An override for one event</option>
            <option value="standalone">A standalone page at its own URL</option>
          </select>
        </label>

        {kind === 'entity_override' && (
          <>
            <label className="field">
              <span className="field__label">Event</span>
              <select className="field__input" value={eventId} required
                onChange={(e) => setEventId(e.target.value)}>
                <option value="">Choose one…</option>
                {events.map((ev) => (
                  <option key={ev.eventId} value={ev.eventId}>{ev.title}</option>
                ))}
              </select>
              <span className="field__help">
                This event stops following its template. Template changes will no longer
                reach it.
              </span>
            </label>

            <label className="field">
              <span className="field__label">Start from</span>
              <select className="field__input" value={startFrom}
                onChange={(e) => setStartFrom(e.target.value as 'resolved' | 'blank')}>
                <option value="resolved">A copy of what this event shows today</option>
                <option value="blank">An empty page</option>
              </select>
              <span className="field__help">
                Copying what it shows today means the override changes nothing until you
                edit it, which is almost always what is wanted.
              </span>
            </label>
          </>
        )}

        {kind === 'standalone' && (
          <>
            <label className="field">
              <span className="field__label">URL path</span>
              <input className="field__input" value={slug} placeholder="about-us"
                onChange={(e) => setSlug(e.target.value)} />
              <span className="field__help">
                Letters, numbers and hyphens. The server normalises it and refuses
                reserved paths.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Organization</span>
              <select className="field__input" value={org} onChange={(e) => setOrg(e.target.value)}>
                <option value="">The platform</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          </>
        )}

        <label className="field">
          <span className="field__label">Name</span>
          <input className="field__input" value={name} maxLength={120}
            required={kind === 'standalone'}
            placeholder={kind === 'entity_override' ? 'Optional — named after the event' : 'About us'}
            onChange={(e) => setName(e.target.value)} />
          <span className="field__help">Internal only. Visitors never see it.</span>
        </label>

        <div className="adm-actions">
          <button type="submit" className="button button--primary"
            disabled={busy || (kind === 'entity_override' ? !eventId : !name.trim())}>
            {busy ? 'Creating…' : 'Create and edit'}
          </button>
          <Link className="button button--ghost" to="/admin/content/pages">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
